import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useBrand } from '../../app/config/brand';
import { useApi, API_BASE_URL } from '../../api/useApi';
import type { CaseListStatus } from '@lds-efsp/shared-contracts';

type TabKey = 'orders' | 'documents' | 'case' | 'contacts' | 'history';

const STATUS_CONFIG: Record<CaseListStatus, { label: string; colorKey: string }> = {
  active: { label: 'Active', colorKey: 'success' },
  pending: { label: 'Pending', colorKey: 'info' },
  needs_attention: { label: 'Needs Attention', colorKey: 'warning' },
  closed: { label: 'Closed', colorKey: 'textMuted' },
};

interface CaseOrder {
  id: string;
  orderNumber: string;
  type: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  courtLocation: string | null;
  county: string | null;
}

interface CaseDocument {
  id: string;
  orderId: string;
  filename: string;
  category: string | null;
  documentType: string | null;
  createdAt: string;
}

interface CaseDetailResponse {
  caseNumber: string;
  caseTitle: string | null;
  caseCategory: string | null;
  county: string | null;
  courtLocation: string | null;
  status: CaseListStatus;
  lastActivity: { date: string; description: string };
  orderCount: number;
  documentCount: number;
  filingType: string;
  createdAt: string;
  orders: CaseOrder[];
  documents: CaseDocument[];
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatRelativeTime(dateString: string): string {
  const now = Date.now();
  const diff = now - new Date(dateString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

const ORDER_TYPE_LABELS: Record<string, string> = {
  'e-filing': 'eFiling',
  'court-filing': 'Court Filing',
  'process-serving': 'Process Serving',
  'document-retrieval': 'Doc Retrieval',
  'courtesy-copy-delivery': 'Courtesy Copy',
  'secretary-of-state-filing': 'SOS Filing',
  'county-recording': 'Recording',
  'skip-trace': 'Skip Trace',
  'eservice': 'eService',
};

interface StatusChipProps {
  status: string;
  statusConfig?: Record<string, { label: string; colorKey: string }>;
}

const StatusChip: React.FC<StatusChipProps> = ({ status, statusConfig }) => {
  const brand = useBrand();
  const colors = brand.colors as Record<string, string>;
  const cfg = statusConfig?.[status] ?? STATUS_CONFIG[status as CaseListStatus];
  if (!cfg) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap"
        style={{ backgroundColor: `${brand.colors.textMuted}18`, color: brand.colors.textMuted }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: brand.colors.textMuted }} />
        {status}
      </span>
    );
  }
  const color = colors[cfg.colorKey] ?? colors.textMuted;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap"
      style={{ backgroundColor: `${color}18`, color }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
      {cfg.label}
    </span>
  );
};

const ORDER_STATUS_CONFIG: Record<string, { label: string; colorKey: string }> = {
  draft: { label: 'Draft', colorKey: 'textMuted' },
  pending: { label: 'Pending', colorKey: 'info' },
  submitted: { label: 'Submitted', colorKey: 'info' },
  in_progress: { label: 'In Progress', colorKey: 'warning' },
  accepted: { label: 'Accepted', colorKey: 'success' },
  completed: { label: 'Completed', colorKey: 'success' },
  closed: { label: 'Closed', colorKey: 'textMuted' },
  rejected: { label: 'Rejected', colorKey: 'error' },
  cancelled: { label: 'Cancelled', colorKey: 'textMuted' },
};

interface TypeChipProps {
  type: string;
}

const TypeChip: React.FC<TypeChipProps> = ({ type }) => {
  const brand = useBrand();
  const isEfiling = type === 'e-filing';
  return (
    <span
      className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{
        backgroundColor: isEfiling ? brand.colors.primaryLight : brand.colors.bgLight,
        color: isEfiling ? brand.colors.link : brand.colors.textLight,
      }}
    >
      {ORDER_TYPE_LABELS[type] ?? type}
    </span>
  );
};

const TAB_ITEMS: { key: TabKey; label: string; countField?: 'orderCount' | 'documentCount' }[] = [
  { key: 'orders', label: 'Orders', countField: 'orderCount' },
  { key: 'documents', label: 'Documents', countField: 'documentCount' },
  { key: 'case', label: 'Case info' },
  { key: 'contacts', label: 'Service contacts' },
  { key: 'history', label: 'Contact history' },
];

interface OrdersTabProps {
  orders: CaseOrder[];
  caseNumber: string;
}

const OrdersTab: React.FC<OrdersTabProps> = ({ orders, caseNumber }) => {
  const brand = useBrand();
  const navigate = useNavigate();

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: brand.colors.secondary }}>
            Orders placed for this case
          </h2>
          <p className="text-sm mt-0.5" style={{ color: brand.colors.textLight }}>
            All filings and service orders you've placed through {brand.company.name} for this case.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            className="px-3 py-1.5 rounded-lg text-sm font-medium"
            style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            onClick={() => navigate(`/orders/new?case=${caseNumber}`)}
          >
            + Place Order
          </button>
        </div>
      </div>

      {orders.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-12 rounded-xl"
          style={{ backgroundColor: brand.colors.bgLight }}
        >
          <p className="text-sm" style={{ color: brand.colors.textLight }}>No orders found for this case.</p>
        </div>
      ) : (
        <div
          className="rounded-xl overflow-hidden"
          style={{ border: `1px solid ${brand.colors.border}`, backgroundColor: brand.colors.bgCard }}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: `1px solid ${brand.colors.border}`, backgroundColor: brand.colors.bgLight }}>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: brand.colors.textLight }}>Order #</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: brand.colors.textLight }}>Type</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider hidden md:table-cell" style={{ color: brand.colors.textLight }}>Court</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: brand.colors.textLight }}>Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: brand.colors.textLight }}>Created</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider hidden lg:table-cell" style={{ color: brand.colors.textLight }}>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr
                    key={order.id}
                    style={{ borderBottom: `1px solid ${brand.colors.border}` }}
                  >
                    <td className="px-4 py-3">
                      <button
                        className="font-mono text-sm font-medium"
                        style={{ color: brand.colors.link }}
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        {order.orderNumber}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <TypeChip type={order.type} />
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div style={{ color: brand.colors.text }}>{order.courtLocation ?? '-'}</div>
                      {order.county && (
                        <div className="text-xs mt-0.5" style={{ color: brand.colors.textLight }}>{order.county}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusChip status={order.status} statusConfig={ORDER_STATUS_CONFIG} />
                    </td>
                    <td className="px-4 py-3" style={{ color: brand.colors.text }}>
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell" style={{ color: brand.colors.textLight }}>
                      {order.submittedAt ? formatDate(order.submittedAt) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

interface DocumentsTabProps {
  documents: CaseDocument[];
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({ documents }) => {
  const brand = useBrand();

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: brand.colors.secondary }}>
            Documents on file
          </h2>
          <p className="text-sm mt-0.5" style={{ color: brand.colors.textLight }}>
            Every document filed or received through {brand.company.name} for this case.
          </p>
        </div>
      </div>

      {documents.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-12 rounded-xl"
          style={{ backgroundColor: brand.colors.bgLight }}
        >
          <p className="text-sm" style={{ color: brand.colors.textLight }}>No documents found for this case.</p>
        </div>
      ) : (
        <div
          className="rounded-xl overflow-hidden"
          style={{ border: `1px solid ${brand.colors.border}`, backgroundColor: brand.colors.bgCard }}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: `1px solid ${brand.colors.border}`, backgroundColor: brand.colors.bgLight }}>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: brand.colors.textLight }}>Document</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider hidden md:table-cell" style={{ color: brand.colors.textLight }}>Type</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: brand.colors.textLight }}>Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr
                    key={doc.id}
                    style={{ borderBottom: `1px solid ${brand.colors.border}` }}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2.5">
                        <svg
                          className="w-5 h-5 mt-0.5 shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke={brand.colors.textMuted}
                          strokeWidth={1.5}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div className="min-w-0">
                          <div className="font-medium truncate max-w-md" style={{ color: brand.colors.text }}>
                            {doc.filename}
                          </div>
                          {doc.category && (
                            <span
                              className="text-xs px-1.5 py-0.5 rounded mt-0.5 inline-block"
                              style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
                            >
                              {doc.category}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell" style={{ color: brand.colors.text }}>
                      {doc.documentType ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div style={{ color: brand.colors.text }}>{formatDate(doc.createdAt)}</div>
                      <div className="text-xs mt-0.5" style={{ color: brand.colors.textLight }}>
                        {formatRelativeTime(doc.createdAt)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

interface CaseInfoTabProps {
  caseData: CaseDetailResponse;
}

const CaseInfoTab: React.FC<CaseInfoTabProps> = ({ caseData }) => {
  const brand = useBrand();

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: brand.colors.secondary }}>
            Case information
          </h2>
          <p className="text-sm mt-0.5" style={{ color: brand.colors.textLight }}>
            Court details, client info, and billing information.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          className="rounded-xl p-5"
          style={{ border: `1px solid ${brand.colors.border}`, backgroundColor: brand.colors.bgCard }}
        >
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke={brand.colors.primary} strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-sm font-semibold" style={{ color: brand.colors.secondary }}>
              Case identification
            </h3>
          </div>
          <dl className="space-y-3">
            <div className="flex justify-between py-2" style={{ borderBottom: `1px solid ${brand.colors.border}` }}>
              <dt className="text-sm" style={{ color: brand.colors.textLight }}>Case #</dt>
              <dd className="text-sm font-mono font-medium" style={{ color: brand.colors.text }}>
                {caseData.caseNumber}
              </dd>
            </div>
            <div className="flex justify-between py-2" style={{ borderBottom: `1px solid ${brand.colors.border}` }}>
              <dt className="text-sm" style={{ color: brand.colors.textLight }}>Case name</dt>
              <dd className="text-sm font-medium text-right max-w-xs truncate" style={{ color: brand.colors.text }}>
                {caseData.caseTitle ?? 'Untitled'}
              </dd>
            </div>
            <div className="flex justify-between py-2" style={{ borderBottom: `1px solid ${brand.colors.border}` }}>
              <dt className="text-sm" style={{ color: brand.colors.textLight }}>Jurisdiction</dt>
              <dd className="text-sm text-right" style={{ color: brand.colors.text }}>
                {[caseData.county, caseData.courtLocation].filter(Boolean).join(' — ') || 'Not set'}
              </dd>
            </div>
            <div className="flex justify-between py-2">
              <dt className="text-sm" style={{ color: brand.colors.textLight }}>Category</dt>
              <dd className="text-sm" style={{ color: caseData.caseCategory ? brand.colors.text : brand.colors.textMuted }}>
                {caseData.caseCategory ?? 'Not set'}
              </dd>
            </div>
          </dl>
        </div>

        <div
          className="rounded-xl p-5"
          style={{ border: `1px solid ${brand.colors.border}`, backgroundColor: brand.colors.bgCard }}
        >
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke={brand.colors.primary} strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-sm font-semibold" style={{ color: brand.colors.secondary }}>
              Client & billing
            </h3>
          </div>
          <dl className="space-y-3">
            <div className="flex justify-between py-2" style={{ borderBottom: `1px solid ${brand.colors.border}` }}>
              <dt className="text-sm" style={{ color: brand.colors.textLight }}>Orders</dt>
              <dd className="text-sm font-medium" style={{ color: brand.colors.text }}>
                {caseData.orderCount} {caseData.orderCount === 1 ? 'order' : 'orders'}
              </dd>
            </div>
            <div className="flex justify-between py-2" style={{ borderBottom: `1px solid ${brand.colors.border}` }}>
              <dt className="text-sm" style={{ color: brand.colors.textLight }}>Documents</dt>
              <dd className="text-sm" style={{ color: brand.colors.text }}>
                {caseData.documentCount} {caseData.documentCount === 1 ? 'document' : 'documents'}
              </dd>
            </div>
            <div className="flex justify-between py-2">
              <dt className="text-sm" style={{ color: brand.colors.textLight }}>Filing type</dt>
              <dd className="text-sm" style={{ color: brand.colors.text }}>
                {caseData.filingType}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

const ServiceContactsTab: React.FC = () => {
  const brand = useBrand();

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: brand.colors.secondary }}>
            Service contact list
          </h2>
          <p className="text-sm mt-0.5" style={{ color: brand.colors.textLight }}>
            Contacts attached to this case for electronic service.
          </p>
        </div>
        <button
          className="px-3 py-1.5 rounded-lg text-sm font-medium"
          style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
        >
          + Add Service Contact
        </button>
      </div>

      <div
        className="flex flex-col items-center justify-center py-12 rounded-xl"
        style={{ backgroundColor: brand.colors.bgLight }}
      >
        <svg className="w-10 h-10 mb-3" fill="none" viewBox="0 0 24 24" stroke={brand.colors.textMuted} strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <p className="text-sm font-medium" style={{ color: brand.colors.secondary }}>No service contacts</p>
        <p className="text-xs mt-1" style={{ color: brand.colors.textLight }}>
          Service contacts will appear here once added to this case.
        </p>
      </div>
    </div>
  );
};

const ContactHistoryTab: React.FC = () => {
  const brand = useBrand();

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: brand.colors.secondary }}>
            Service contact history
          </h2>
          <p className="text-sm mt-0.5" style={{ color: brand.colors.textLight }}>
            Audit trail of contacts added or removed from this case.
          </p>
        </div>
      </div>

      <div
        className="flex flex-col items-center justify-center py-12 rounded-xl"
        style={{ backgroundColor: brand.colors.bgLight }}
      >
        <svg className="w-10 h-10 mb-3" fill="none" viewBox="0 0 24 24" stroke={brand.colors.textMuted} strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
        </svg>
        <p className="text-sm font-medium" style={{ color: brand.colors.secondary }}>No contact history</p>
        <p className="text-xs mt-1" style={{ color: brand.colors.textLight }}>
          Contact changes will be recorded here.
        </p>
      </div>
    </div>
  );
};

const CaseDetailPage: React.FC = () => {
  const { caseNumber } = useParams<{ caseNumber: string }>();
  const navigate = useNavigate();
  const brand = useBrand();
  const { get } = useApi();

  const [data, setData] = useState<CaseDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('orders');

  useEffect(() => {
    if (!caseNumber) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await get<CaseDetailResponse>(`${API_BASE_URL}/api/cases/${encodeURIComponent(caseNumber)}`);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load case details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [caseNumber, get]);

  if (loading) {
    return (
      <div>
        <div className="h-6 w-40 rounded animate-pulse mb-6" style={{ backgroundColor: brand.colors.bgLight }} />
        <div className="h-8 w-64 rounded animate-pulse mb-4" style={{ backgroundColor: brand.colors.bgLight }} />
        <div className="h-32 rounded-xl animate-pulse mb-6" style={{ backgroundColor: brand.colors.bgLight }} />
        <div className="h-64 rounded-xl animate-pulse" style={{ backgroundColor: brand.colors.bgLight }} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        <button
          className="flex items-center gap-1 text-sm font-medium mb-6"
          style={{ color: brand.colors.link }}
          onClick={() => navigate('/cases')}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to all cases
        </button>
        <div
          className="rounded-xl p-6 text-center"
          style={{ backgroundColor: '#FEE2E2' }}
        >
          <p className="text-sm font-medium" style={{ color: brand.colors.error }}>
            {error ?? 'Case not found'}
          </p>
        </div>
      </div>
    );
  }

  const caseDetail = data;
  const colors = brand.colors as Record<string, string>;
  const statusCfg = STATUS_CONFIG[caseDetail.status];
  const statusColor = colors[statusCfg.colorKey] ?? colors.textMuted;

  return (
    <div>
      <button
        className="flex items-center gap-1 text-sm font-medium mb-6"
        style={{ color: brand.colors.link }}
        onClick={() => navigate('/cases')}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back to all cases
      </button>

      <section className="mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
                style={{ backgroundColor: `${statusColor}18`, color: statusColor }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusColor }} />
                {statusCfg.label}
              </span>
              <span className="text-sm font-mono" style={{ color: brand.colors.textLight }}>
                {caseDetail.caseNumber}
              </span>
            </div>
            <h1
              className="text-2xl font-semibold mb-2"
              style={{ color: brand.colors.secondary, fontFamily: brand.typography.fontFamily.heading }}
            >
              {caseDetail.caseTitle ?? 'Untitled Case'}
            </h1>
            <div className="flex flex-wrap items-center gap-2 text-sm" style={{ color: brand.colors.textLight }}>
              {caseDetail.county && (
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {caseDetail.county}
                  {caseDetail.courtLocation && ` — ${caseDetail.courtLocation}`}
                </span>
              )}
              {caseDetail.caseCategory && (
                <>
                  <span style={{ color: brand.colors.textMuted }}>&middot;</span>
                  <span>{caseDetail.caseCategory}</span>
                </>
              )}
              <span style={{ color: brand.colors.textMuted }}>&middot;</span>
              <span>
                Last activity <strong style={{ color: brand.colors.text }}>{formatDate(caseDetail.lastActivity.date)}</strong>
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              className="w-9 h-9 flex items-center justify-center rounded-lg text-lg"
              style={{
                border: `1px solid ${brand.colors.border}`,
                color: brand.colors.textLight,
                backgroundColor: brand.colors.bgCard,
              }}
            >
              &hellip;
            </button>
            <button
              className="px-4 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
              onClick={() => navigate(`/orders/new?case=${caseDetail.caseNumber}`)}
            >
              + Place Order
            </button>
          </div>
        </div>
      </section>

      <nav
        className="flex gap-0 mb-6"
        style={{ borderBottom: `2px solid ${brand.colors.border}` }}
        role="tablist"
      >
        {TAB_ITEMS.map((tab) => {
          const isActive = activeTab === tab.key;
          const count = tab.countField ? caseDetail[tab.countField] : undefined;
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              className="relative px-4 py-3 text-sm font-medium transition-colors flex items-center gap-1.5"
              style={{
                color: isActive ? brand.colors.link : brand.colors.textLight,
              }}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              {count != null && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded-full"
                  style={{
                    backgroundColor: isActive ? brand.colors.primaryLight : brand.colors.bgLight,
                    color: isActive ? brand.colors.link : brand.colors.textMuted,
                  }}
                >
                  {count}
                </span>
              )}
              {isActive && (
                <span
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ backgroundColor: brand.colors.primary }}
                />
              )}
            </button>
          );
        })}
      </nav>

      <div>
        {activeTab === 'orders' && (
          <OrdersTab orders={data.orders} caseNumber={caseDetail.caseNumber} />
        )}
        {activeTab === 'documents' && (
          <DocumentsTab documents={data.documents} />
        )}
        {activeTab === 'case' && (
          <CaseInfoTab caseData={caseDetail} />
        )}
        {activeTab === 'contacts' && (
          <ServiceContactsTab />
        )}
        {activeTab === 'history' && (
          <ContactHistoryTab />
        )}
      </div>
    </div>
  );
};

export default CaseDetailPage;
